import { useMemo, useState, type HTMLAttributes, type PointerEvent as ReactPointerEvent } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@shared/utils/cn';

type JsonSchemaTreeMode = 'standard' | 'input' | 'output';

const OUTPUT_INDENT_STEP_PX = 44; // matches ml-7 (28px) + pl-4 (16px)

type JsonSchema = {
  type?: string | string[];
  title?: string;
  description?: string;
  format?: string;
  enum?: unknown[];
  const?: unknown;
  default?: unknown;
  examples?: unknown[];
  properties?: Record<string, JsonSchema>;
  patternProperties?: Record<string, JsonSchema>;
  additionalProperties?: boolean | JsonSchema;
  items?: JsonSchema | JsonSchema[];
  required?: string[];
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  exclusiveMaximum?: number;
  multipleOf?: number;
  anyOf?: JsonSchema[];
  oneOf?: JsonSchema[];
  allOf?: JsonSchema[];
  $ref?: string;
  [key: string]: unknown;
};

type SchemaPinContext = {
  path: string[];
  pathString: string;
  schema: JsonSchema;
  required: boolean;
  typeTokens: string[];
};

type SchemaPinHandlers = {
  onPointerDown?: (context: SchemaPinContext, event: ReactPointerEvent<HTMLButtonElement>) => void;
  onPointerUp?: (context: SchemaPinContext, event: ReactPointerEvent<HTMLButtonElement>) => void;
  onPointerEnter?: (context: SchemaPinContext, event: ReactPointerEvent<HTMLButtonElement>) => void;
  onPointerLeave?: (context: SchemaPinContext, event: ReactPointerEvent<HTMLButtonElement>) => void;
};

export interface JsonSchemaTreeProps {
  schema: JsonSchema;
  title?: string;
  className?: string;
  defaultExpandedDepth?: number;
  maxDepth?: number;
  mode?: JsonSchemaTreeMode;
  interactivePins?: boolean;
  leafPinsOnly?: boolean;
  onPinPointerDown?: SchemaPinHandlers['onPointerDown'];
  onPinPointerUp?: SchemaPinHandlers['onPointerUp'];
  onPinPointerEnter?: SchemaPinHandlers['onPointerEnter'];
  onPinPointerLeave?: SchemaPinHandlers['onPointerLeave'];
  pendingPath?: string | null;
  activePath?: string | null;
}

const FALLBACK_TYPE = 'any';
const PRIMITIVE_TYPES = new Set(['string', 'number', 'integer', 'boolean', 'null']);

const TYPE_COLOR_MAP: Record<string, string> = {
  string: 'var(--type-string)',
  number: 'var(--type-number)',
  integer: 'var(--type-number)',
  boolean: 'var(--type-boolean)',
  object: 'var(--type-object)',
  array: 'var(--type-array)',
  enum: 'var(--type-string)',
  any: 'var(--type-any)',
  null: 'var(--type-any)',
  unknown: 'var(--type-any)',
};

function resolveTypeColor(type: string): string {
  const key = type.toLowerCase();
  return TYPE_COLOR_MAP[key] ?? TYPE_COLOR_MAP.any;
}

function deriveTypeTokens(schema: JsonSchema): string[] {
  if (Array.isArray(schema.type) && schema.type.length > 0) {
    return schema.type.map((value) => String(value));
  }

  if (typeof schema.type === 'string' && schema.type.trim().length > 0) {
    return [schema.type];
  }

  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return ['enum'];
  }

  if (schema.properties) {
    return ['object'];
  }

  if (schema.items) {
    return ['array'];
  }

  return [FALLBACK_TYPE];
}

function stringifyValue(value: unknown): string {
  try {
    if (typeof value === 'string') {
      return `"${value}"`;
    }

    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
}

function buildPathString(path: string[]): string {
  const parts: string[] = [];
  path.forEach((segment, index) => {
    if (!segment || segment === '<root>') return;
    if (index === 0) return; // skip root label
    if (segment === 'items') {
      if (parts.length > 0) {
        parts[parts.length - 1] = `${parts[parts.length - 1]}[]`;
      }
      return;
    }
    if (segment.startsWith('[')) {
      if (parts.length > 0) {
        parts[parts.length - 1] = `${parts[parts.length - 1]}${segment}`;
      } else {
        parts.push(segment);
      }
      return;
    }
    parts.push(segment);
  });
  return parts.join('.');
}

interface SchemaNodeProps {
  name: string | null;
  schema: JsonSchema;
  depth: number;
  defaultExpandedDepth: number;
  maxDepth: number;
  required: boolean;
  path: string[];
  mode: JsonSchemaTreeMode;
  interactivePins: boolean;
  leafPinsOnly: boolean;
  pinHandlers: SchemaPinHandlers;
  pendingPath?: string | null;
  activePath?: string | null;
}

interface SchemaNodeChildrenProps {
  schema: JsonSchema;
  depth: number;
  defaultExpandedDepth: number;
  maxDepth: number;
  path: string[];
  mode: JsonSchemaTreeMode;
  interactivePins: boolean;
  leafPinsOnly: boolean;
  pinHandlers: SchemaPinHandlers;
  pendingPath?: string | null;
  activePath?: string | null;
}

function isPrimitiveSchema(schema: JsonSchema | boolean | undefined): boolean {
  if (!schema) return true;
  if (typeof schema === 'boolean') return schema;

  const types = Array.isArray(schema.type)
    ? schema.type.map((value) => String(value))
    : typeof schema.type === 'string'
      ? [schema.type]
      : [];

  if (types.some((value) => value === 'object' || value === 'array')) {
    return false;
  }

  const hasProps = schema.properties && Object.keys(schema.properties).length > 0;
  if (hasProps) return false;

  const hasPattern = schema.patternProperties && Object.keys(schema.patternProperties).length > 0;
  if (hasPattern) return false;

  const hasCombos =
    (Array.isArray(schema.anyOf) && schema.anyOf.length > 0) ||
    (Array.isArray(schema.oneOf) && schema.oneOf.length > 0) ||
    (Array.isArray(schema.allOf) && schema.allOf.length > 0);
  if (hasCombos) return false;

  if (typeof schema.additionalProperties === 'object') {
    return false;
  }

  if (Array.isArray(schema.items) && schema.items.length > 0) {
    return false;
  }

  if (schema.items && !Array.isArray(schema.items)) {
    return false;
  }

  if (types.length === 0) {
    return true;
  }

  return types.every((value) => PRIMITIVE_TYPES.has(value));
}

function hasNestedChildren(schema: JsonSchema | boolean | undefined): boolean {
  if (!schema || typeof schema === 'boolean') {
    return false;
  }

  const props = schema.properties && Object.keys(schema.properties).length > 0;
  if (props) return true;

  const pattern = schema.patternProperties && Object.keys(schema.patternProperties).length > 0;
  if (pattern) return true;

  if (Array.isArray(schema.items)) {
    return schema.items.length > 0;
  }

  if (schema.items && !isPrimitiveSchema(schema.items)) {
    return true;
  }

  const hasCombos =
    (Array.isArray(schema.anyOf) && schema.anyOf.length > 0) ||
    (Array.isArray(schema.oneOf) && schema.oneOf.length > 0) ||
    (Array.isArray(schema.allOf) && schema.allOf.length > 0);
  if (hasCombos) return true;

  if (typeof schema.additionalProperties === 'object') {
    return true;
  }

  return false;
}

function schemaHasChildren(schema: JsonSchema): boolean {
  return hasNestedChildren(schema);
}

function isArrayOfPrimitives(schema: JsonSchema): boolean {
  const types = Array.isArray(schema.type)
    ? schema.type.map((value) => String(value))
    : typeof schema.type === 'string'
      ? [schema.type]
      : [];

  const isArrayType = types.includes('array') || !!schema.items;
  if (!isArrayType) {
    return false;
  }

  if (Array.isArray(schema.items)) {
    return false;
  }

  if (!schema.items) {
    return true;
  }

  if (typeof schema.items !== 'object' || schema.items === null) {
    return true;
  }

  return isPrimitiveSchema(schema.items as JsonSchema);
}

type PinPlacement = 'inline' | 'end' | 'none';

// `depth` can begin at -1 when invoked for the root-level render to keep output pins aligned.
function SchemaNodeChildren({
  schema,
  depth,
  defaultExpandedDepth,
  maxDepth,
  path,
  mode,
  interactivePins,
  leafPinsOnly,
  pinHandlers,
  pendingPath,
  activePath,
}: SchemaNodeChildrenProps) {
  const childRequired = useMemo(() => new Set(schema.required ?? []), [schema.required]);
  const propertyEntries = Object.entries(schema.properties ?? {});
  const patternEntries = Object.entries(schema.patternProperties ?? {});
  const tupleItems = Array.isArray(schema.items) ? schema.items : undefined;
  const singleItemSchema =
    !Array.isArray(schema.items) && typeof schema.items === 'object' && schema.items !== null
      ? (schema.items as JsonSchema)
      : undefined;
  const shouldRenderSingleItems = !!singleItemSchema && !isPrimitiveSchema(singleItemSchema);

  return (
    <>
      {propertyEntries.map(([propName, propSchema]) => (
        <SchemaNode
          key={`property-${propName}`}
          name={propName}
          schema={propSchema}
          depth={depth + 1}
          defaultExpandedDepth={defaultExpandedDepth}
          maxDepth={maxDepth}
          required={childRequired.has(propName)}
          path={[...path, propName]}
          mode={mode}
          interactivePins={interactivePins}
          leafPinsOnly={leafPinsOnly}
          pinHandlers={pinHandlers}
          pendingPath={pendingPath}
          activePath={activePath}
        />
      ))}

      {patternEntries.map(([pattern, patternSchema]) => (
        <SchemaNode
          key={`pattern-${pattern}`}
          name={`/${pattern}/`}
          schema={patternSchema}
          depth={depth + 1}
          defaultExpandedDepth={defaultExpandedDepth}
          maxDepth={maxDepth}
          required={false}
          path={[...path, pattern]}
          mode={mode}
          interactivePins={interactivePins}
          leafPinsOnly={leafPinsOnly}
          pinHandlers={pinHandlers}
          pendingPath={pendingPath}
          activePath={activePath}
        />
      ))}

      {tupleItems?.map((itemSchema, index) => (
        <SchemaNode
          key={`tuple-${index}`}
          name={`[${index}]`}
          schema={itemSchema}
          depth={depth + 1}
          defaultExpandedDepth={defaultExpandedDepth}
          maxDepth={maxDepth}
          required={true}
          path={[...path, String(index)]}
          mode={mode}
          interactivePins={interactivePins}
          leafPinsOnly={leafPinsOnly}
          pinHandlers={pinHandlers}
          pendingPath={pendingPath}
          activePath={activePath}
        />
      ))}

      {shouldRenderSingleItems && (
        <SchemaNode
          key="items"
          name="items"
          schema={singleItemSchema}
          depth={depth + 1}
          defaultExpandedDepth={defaultExpandedDepth}
          maxDepth={maxDepth}
          required={false}
          path={[...path, 'items']}
          mode={mode}
          interactivePins={interactivePins}
          leafPinsOnly={leafPinsOnly}
          pinHandlers={pinHandlers}
          pendingPath={pendingPath}
          activePath={activePath}
        />
      )}

      {schema.anyOf?.map((candidate, index) => (
        <SchemaNode
          key={`anyOf-${index}`}
          name={`anyOf[${index}]`}
          schema={candidate}
          depth={depth + 1}
          defaultExpandedDepth={defaultExpandedDepth}
          maxDepth={maxDepth}
          required={false}
          path={[...path, `anyOf[${index}]`]}
          mode={mode}
          interactivePins={interactivePins}
          leafPinsOnly={leafPinsOnly}
          pinHandlers={pinHandlers}
          pendingPath={pendingPath}
          activePath={activePath}
        />
      ))}

      {schema.oneOf?.map((candidate, index) => (
        <SchemaNode
          key={`oneOf-${index}`}
          name={`oneOf[${index}]`}
          schema={candidate}
          depth={depth + 1}
          defaultExpandedDepth={defaultExpandedDepth}
          maxDepth={maxDepth}
          required={false}
          path={[...path, `oneOf[${index}]`]}
          mode={mode}
          interactivePins={interactivePins}
          leafPinsOnly={leafPinsOnly}
          pinHandlers={pinHandlers}
          pendingPath={pendingPath}
          activePath={activePath}
        />
      ))}

      {schema.allOf?.map((candidate, index) => (
        <SchemaNode
          key={`allOf-${index}`}
          name={`allOf[${index}]`}
          schema={candidate}
          depth={depth + 1}
          defaultExpandedDepth={defaultExpandedDepth}
          maxDepth={maxDepth}
          required={false}
          path={[...path, `allOf[${index}]`]}
          mode={mode}
          interactivePins={interactivePins}
          leafPinsOnly={leafPinsOnly}
          pinHandlers={pinHandlers}
          pendingPath={pendingPath}
          activePath={activePath}
        />
      ))}

      {typeof schema.additionalProperties === 'object' && (
        <SchemaNode
          key="additionalProperties"
          name="additionalProperties"
          schema={schema.additionalProperties as JsonSchema}
          depth={depth + 1}
          defaultExpandedDepth={defaultExpandedDepth}
          maxDepth={maxDepth}
          required={false}
          path={[...path, 'additionalProperties']}
          mode={mode}
          interactivePins={interactivePins}
          leafPinsOnly={leafPinsOnly}
          pinHandlers={pinHandlers}
          pendingPath={pendingPath}
          activePath={activePath}
        />
      )}
    </>
  );
}

type TypePinsProps = {
  types: string[];
  context: SchemaPinContext;
  handlers: SchemaPinHandlers;
  interactive: boolean;
  isActive: boolean;
  isPending: boolean;
};

function TypePins({ types, context, handlers, interactive, isActive, isPending }: TypePinsProps) {
  const uniqueTypes = Array.from(
    new Set(
      types
        .map((type) => type.trim())
        .filter((type) => type.length > 0)
        .map((type) => type.toLowerCase()),
    ),
  );

  const resolved = uniqueTypes.length > 0 ? uniqueTypes : [FALLBACK_TYPE];

  const hasPointerHandlers = Boolean(
    handlers.onPointerDown || handlers.onPointerUp || handlers.onPointerEnter || handlers.onPointerLeave,
  );

  return (
    <div className="flex items-center gap-1">
      {resolved.map((type) => {
        const eventHandlers: HTMLAttributes<HTMLButtonElement> = {};
        if (handlers.onPointerDown) {
          eventHandlers.onPointerDown = (event) => handlers.onPointerDown?.(context, event);
        }
        if (handlers.onPointerUp) {
          eventHandlers.onPointerUp = (event) => handlers.onPointerUp?.(context, event);
        }
        if (handlers.onPointerEnter) {
          eventHandlers.onPointerEnter = (event) => handlers.onPointerEnter?.(context, event);
        }
        if (handlers.onPointerLeave) {
          eventHandlers.onPointerLeave = (event) => handlers.onPointerLeave?.(context, event);
        }
        const buttonClass = cn(
          'inline-flex h-4 w-4 items-center justify-center rounded-full border-[3px] border-current bg-background transition-[transform,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
          (interactive || hasPointerHandlers) && 'cursor-crosshair hover:scale-[1.08]',
          isActive && 'ring-2 ring-primary/60 shadow-[0_0_0_1px_hsla(var(--primary),0.35)]',
          !isActive && isPending && 'ring-1 ring-primary/40 shadow-[0_0_0_1px_hsla(var(--primary),0.25)]',
        );
        return (
          <Tooltip key={`${context.pathString}-${type}`}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={buttonClass}
                style={{ color: resolveTypeColor(type) }}
                aria-label={`${type} type`}
                data-schema-path={context.pathString}
                {...eventHandlers}
              >
                <span className="sr-only">{type}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">{type}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

type NodeHeaderProps = {
  name: string | null;
  badges: Array<{ label: string; variant?: 'default' | 'secondary' | 'outline'; title?: string }>;
  typeTokens: string[];
  placement: PinPlacement;
  interactive: boolean;
  handlers: SchemaPinHandlers;
  context: SchemaPinContext;
  isActive: boolean;
  isPending: boolean;
};

function NodeHeader({
  name,
  badges,
  typeTokens,
  placement,
  interactive,
  handlers,
  context,
  isActive,
  isPending,
}: NodeHeaderProps) {
  const nameEl = name ? <span className="font-medium text-foreground">{name}</span> : null;
  const badgeEls = badges.map((badge, index) => (
    <Badge key={`${badge.label}-${index}`} variant={badge.variant ?? 'secondary'}>
      {badge.label}
    </Badge>
  ));

  const highlightClass = isActive
    ? 'rounded-sm border border-primary/40 bg-primary/10 px-1 py-0.5'
    : isPending
      ? 'rounded-sm border border-primary/20 bg-primary/5 px-1 py-0.5'
      : undefined;

  if (placement === 'end') {
    return (
      <div className={cn('flex w-full items-center gap-2', highlightClass)}>
        <div className="flex flex-wrap items-center gap-2">
          {nameEl}
          {badgeEls}
        </div>
        <div className="ml-auto flex-shrink-0">
          <TypePins
            types={typeTokens}
            handlers={handlers}
            context={context}
            interactive={interactive}
            isActive={isActive}
            isPending={isPending}
          />
        </div>
      </div>
    );
  }

  if (placement === 'none') {
    return (
      <div className={cn('flex flex-wrap items-center gap-2', highlightClass)}>
        {nameEl}
        {badgeEls}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', highlightClass)}>
      {nameEl}
      <TypePins
        types={typeTokens}
        handlers={handlers}
        context={context}
        interactive={interactive}
        isActive={isActive}
        isPending={isPending}
      />
      {badgeEls}
    </div>
  );
}

function SchemaNode({
  name,
  schema,
  depth,
  defaultExpandedDepth,
  maxDepth,
  required,
  path,
  mode,
  interactivePins,
  leafPinsOnly,
  pinHandlers,
  pendingPath,
  activePath,
}: SchemaNodeProps) {
  const typeTokens = deriveTypeTokens(schema);
  const [isExpanded, setIsExpanded] = useState(depth < defaultExpandedDepth);
  const pathString = buildPathString(path);
  const isHighlighted = activePath === pathString;
  const isPending = pendingPath === pathString;
  const pinPlacement: PinPlacement = mode === 'output' ? 'none' : mode === 'input' ? 'end' : 'inline';
  const hasChildren = schemaHasChildren(schema);
  const allowPinInteraction =
    interactivePins && pathString.length > 0 && (!leafPinsOnly || !hasChildren);
  const pinContext: SchemaPinContext = useMemo(
    () => ({ path, pathString, schema, required, typeTokens }),
    [path, pathString, schema, required, typeTokens],
  );
  const rowClass = mode === 'output' ? 'grid grid-cols-[28px_auto_1fr] items-start gap-x-2' : 'flex items-start gap-2';
  const indentOffset = depth * OUTPUT_INDENT_STEP_PX;
  const rowStyle = mode === 'output' ? { marginLeft: -indentOffset } : undefined;
  const nestedColumnStyle = mode === 'output' ? { paddingLeft: indentOffset } : undefined;

  if (depth >= maxDepth) {
    return (
      <div className="text-xs text-muted-foreground italic">
        Max depth ({maxDepth}) reached at <code>{path.join('.')}</code>
      </div>
    );
  }

  const badges: Array<{ label: string; variant?: 'default' | 'secondary' | 'outline'; title?: string }> = [];

  if (required) {
    badges.push({ label: 'required', variant: 'secondary' });
  }

  if (schema.format) {
    badges.push({ label: schema.format, variant: 'outline', title: 'format' });
  }

  if (schema.const !== undefined) {
    badges.push({ label: `const ${stringifyValue(schema.const)}`, variant: 'outline' });
  }

  if (schema.default !== undefined) {
    badges.push({ label: `default ${stringifyValue(schema.default)}`, variant: 'outline' });
  }

  if (schema.$ref) {
    badges.push({ label: `$ref ${schema.$ref}`, variant: 'outline' });
  }

  if (typeof schema.minLength === 'number') {
    badges.push({ label: `minLength ${schema.minLength}`, variant: 'outline' });
  }

  if (typeof schema.maxLength === 'number') {
    badges.push({ label: `maxLength ${schema.maxLength}`, variant: 'outline' });
  }

  if (schema.pattern) {
    badges.push({ label: `pattern ${schema.pattern}`, variant: 'outline' });
  }

  if (typeof schema.minItems === 'number') {
    badges.push({ label: `minItems ${schema.minItems}`, variant: 'outline' });
  }

  if (typeof schema.maxItems === 'number') {
    badges.push({ label: `maxItems ${schema.maxItems}`, variant: 'outline' });
  }

  if (schema.uniqueItems) {
    badges.push({ label: 'uniqueItems', variant: 'outline' });
  }

  if (isArrayOfPrimitives(schema)) {
    const itemSchema =
      !Array.isArray(schema.items) && typeof schema.items === 'object' && schema.items !== null
        ? (schema.items as JsonSchema)
        : undefined;
    const itemTokens = itemSchema ? deriveTypeTokens(itemSchema) : [FALLBACK_TYPE];
    const itemLabel = itemTokens.length > 0 ? itemTokens.join(' | ') : FALLBACK_TYPE;
    badges.push({ label: `${itemLabel}[]`, variant: 'outline', title: 'array items type' });
  }

  if (typeof schema.minimum === 'number') {
    badges.push({ label: `min ${schema.minimum}`, variant: 'outline' });
  }

  if (typeof schema.maximum === 'number') {
    badges.push({ label: `max ${schema.maximum}`, variant: 'outline' });
  }

  if (typeof schema.exclusiveMinimum === 'number') {
    badges.push({ label: `exclusiveMin ${schema.exclusiveMinimum}`, variant: 'outline' });
  }

  if (typeof schema.exclusiveMaximum === 'number') {
    badges.push({ label: `exclusiveMax ${schema.exclusiveMaximum}`, variant: 'outline' });
  }

  if (typeof schema.multipleOf === 'number') {
    badges.push({ label: `multipleOf ${schema.multipleOf}`, variant: 'outline' });
  }

  if (schema.additionalProperties === true) {
    badges.push({ label: 'additional properties ok', variant: 'outline' });
  } else if (schema.additionalProperties === false) {
    badges.push({ label: 'additional properties off', variant: 'outline' });
  }

  const enumValues = Array.isArray(schema.enum) ? schema.enum : undefined;

  const nextHandlers = allowPinInteraction ? pinHandlers : {};

  const controlElement = hasChildren ? (
    <Button
      size="sm"
      variant="ghost"
      className="mt-0.5 h-6 w-6 shrink-0 p-0"
      onClick={() => setIsExpanded((current) => !current)}
      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${name ?? 'schema node'}`}
    >
      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </Button>
  ) : (
    <div className="mt-0.5 h-6 w-6 shrink-0" />
  );

  const controlCell = mode === 'output' ? <div style={nestedColumnStyle}>{controlElement}</div> : controlElement;

  return (
    <div className="space-y-2 text-sm">
      <div className={cn(rowClass)} style={rowStyle}>
        {mode === 'output' && (
          <div className="mt-0.5 flex h-6 items-center">
            <TypePins
              types={typeTokens}
              handlers={nextHandlers}
              context={pinContext}
              interactive={allowPinInteraction}
              isActive={isHighlighted}
              isPending={isPending}
            />
          </div>
        )}
        {controlCell}

        <div
          className={cn('space-y-1', mode !== 'output' && 'flex-1')}
          style={nestedColumnStyle}
        >
          <NodeHeader
            name={name}
            badges={badges}
            typeTokens={typeTokens}
            placement={pinPlacement}
            interactive={allowPinInteraction}
            handlers={nextHandlers}
            context={pinContext}
            isActive={isHighlighted}
            isPending={isPending}
          />

          {schema.title && <div className="text-xs text-muted-foreground">{schema.title}</div>}

          {schema.description && (
            <p className="text-xs leading-relaxed text-muted-foreground">{schema.description}</p>
          )}

          {enumValues && enumValues.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">enum</span>: {enumValues.map(stringifyValue).join(', ')}
            </div>
          )}

          {schema.examples && schema.examples.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">examples</span>: {schema.examples.map(stringifyValue).join(', ')}
            </div>
          )}
        </div>
      </div>

      {hasChildren && isExpanded ? (
        <div className="ml-7 border-l border-border pl-4">
          <SchemaNodeChildren
            schema={schema}
            depth={depth}
            defaultExpandedDepth={defaultExpandedDepth}
            maxDepth={maxDepth}
            path={path}
            mode={mode}
            interactivePins={interactivePins}
            leafPinsOnly={leafPinsOnly}
            pinHandlers={pinHandlers}
            pendingPath={pendingPath}
            activePath={activePath}
          />
        </div>
      ) : null}
    </div>
  );
}

export function JsonSchemaTree({
  schema,
  title,
  className,
  defaultExpandedDepth = 1,
  maxDepth = 12,
  mode = 'standard',
  interactivePins = false,
  leafPinsOnly = true,
  onPinPointerDown,
  onPinPointerUp,
  onPinPointerEnter,
  onPinPointerLeave,
  pendingPath,
  activePath,
}: JsonSchemaTreeProps) {
  const rootName = title ?? schema.title ?? null;
  const schemaToRender = schema ?? {};
  const rootPath = rootName ? [rootName] : ['<root>'];
  const hasRenderableChildren = schemaHasChildren(schemaToRender);
  const handlers: SchemaPinHandlers = useMemo(
    () => ({
      onPointerDown: onPinPointerDown,
      onPointerUp: onPinPointerUp,
      onPointerEnter: onPinPointerEnter,
      onPointerLeave: onPinPointerLeave,
    }),
    [onPinPointerDown, onPinPointerUp, onPinPointerEnter, onPinPointerLeave],
  );

  return (
    <ScrollArea className={cn('h-[420px] w-full overflow-hidden', className)}>
      <div className="space-y-3 p-4">
        {hasRenderableChildren ? (
          <>
            {(rootName || schemaToRender.description) && (
              <div className="space-y-1 text-sm">
                {rootName && <div className="font-semibold text-foreground">{rootName}</div>}
                {schemaToRender.description && (
                  <p className="text-xs leading-relaxed text-muted-foreground">{schemaToRender.description}</p>
                )}
              </div>
            )}

            <SchemaNodeChildren
              schema={schemaToRender}
              depth={-1}
              defaultExpandedDepth={defaultExpandedDepth}
              maxDepth={maxDepth}
              path={rootPath}
              mode={mode}
              interactivePins={interactivePins}
              leafPinsOnly={leafPinsOnly}
              pinHandlers={handlers}
              pendingPath={pendingPath}
              activePath={activePath}
            />
          </>
        ) : (
          <SchemaNode
            name={rootName}
            schema={schemaToRender}
            depth={0}
            defaultExpandedDepth={defaultExpandedDepth}
            maxDepth={maxDepth}
            required={false}
            path={rootPath}
            mode={mode}
            interactivePins={interactivePins}
            leafPinsOnly={leafPinsOnly}
            pinHandlers={handlers}
            pendingPath={pendingPath}
            activePath={activePath}
          />
        )}
      </div>
    </ScrollArea>
  );
}

export type { JsonSchemaTreeMode, SchemaPinContext };
